import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { Content, EnumTableData } from "./schema"
import { DataStatus } from "../enums"

export interface ContentManagementState {
  content: Content
  contentDataStatus: DataStatus
  providerEnumData: EnumTableData
  practiceEnumData: EnumTableData
  providerTableDataStatus: DataStatus
  practiceTableDataStatus: DataStatus
  // New lazy loading properties
  loadedCollections: Record<keyof Content, boolean>
  collectionLoadingStatus: Record<keyof Content, DataStatus>
  // GraphQL override properties (for QA testing in-work content)
  isContentOverridden: boolean
  overrideContent: Content | null
}

const initialContent: Content = {} // start with all undefined
const initialProviderEnumData: EnumTableData = {}
const initialPracticeEnumData: EnumTableData = {}

const initialState: ContentManagementState = {
  content: initialContent,
  contentDataStatus: DataStatus.PENDING,
  providerEnumData: initialProviderEnumData,
  practiceEnumData: initialPracticeEnumData,
  providerTableDataStatus: DataStatus.LOADING,
  practiceTableDataStatus: DataStatus.LOADING,
  loadedCollections: {} as Record<keyof Content, boolean>,
  collectionLoadingStatus: {} as Record<keyof Content, DataStatus>,
  isContentOverridden: false,
  overrideContent: null,
}

export const contentManagementSlice = createSlice({
  name: "contentManagement",
  initialState,
  reducers: {
    contentUpdated: (
      state: { content: Content },
      { payload }: PayloadAction<Partial<Content>>,
    ) => {
      state.content = {
        ...state.content,
        ...payload,
      }
    },
    providerTableDataUpdated: (
      state: { providerEnumData: EnumTableData },
      { payload }: PayloadAction<EnumTableData>,
    ) => {
      state.providerEnumData = payload
    },
    practiceTableDataUpdated: (
      state: { practiceEnumData: EnumTableData },
      { payload }: PayloadAction<EnumTableData>,
    ) => {
      state.practiceEnumData = payload
    },
    contentDataStatusUpdated: (
      state: { contentDataStatus: DataStatus },
      { payload }: PayloadAction<DataStatus>,
    ) => {
      state.contentDataStatus = payload
    },
    providerTableDataStatusUpdated: (
      state: { providerTableDataStatus: DataStatus },
      { payload }: PayloadAction<DataStatus>,
    ) => {
      state.providerTableDataStatus = payload
    },
    practiceTableDataStatusUpdated: (
      state: { practiceTableDataStatus: DataStatus },
      { payload }: PayloadAction<DataStatus>,
    ) => {
      state.practiceTableDataStatus = payload
    },
    // New lazy loading actions
    collectionContentUpdated: (
      state,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { payload }: PayloadAction<{ collectionName: keyof Content; data: any }>,
    ) => {
      state.content[payload.collectionName] = payload.data
      state.loadedCollections[payload.collectionName] = true
    },
    collectionLoadingStatusUpdated: (
      state,
      {
        payload,
      }: PayloadAction<{ collectionName: keyof Content; status: DataStatus }>,
    ) => {
      state.collectionLoadingStatus[payload.collectionName] = payload.status
    },
    // GraphQL override actions
    overrideContentSet: (state, { payload }: PayloadAction<Content>) => {
      state.isContentOverridden = true
      state.overrideContent = payload
      // Mark all collections as loaded since we're overriding with full content
      Object.keys(payload).forEach((key) => {
        state.loadedCollections[key as keyof Content] = true
        state.collectionLoadingStatus[key as keyof Content] = DataStatus.LOADED
      })
    },
    overrideContentCleared: (state) => {
      state.isContentOverridden = false
      state.overrideContent = null
      // Clear content and loading status to force reload from files
      state.content = {} as Content
      state.loadedCollections = {} as Record<keyof Content, boolean>
      state.collectionLoadingStatus = {} as Record<keyof Content, DataStatus>
    },
  },
})

export const {
  contentUpdated,
  providerTableDataUpdated,
  practiceTableDataUpdated,
  contentDataStatusUpdated,
  providerTableDataStatusUpdated,
  practiceTableDataStatusUpdated,
  collectionContentUpdated,
  collectionLoadingStatusUpdated,
  overrideContentSet,
  overrideContentCleared,
} = contentManagementSlice.actions

export const selectContent = (state: {
  contentManagement: ContentManagementState
}): Content => {
  return state.contentManagement.content
}

export const selectProviderEnumTableData = (state: {
  contentManagement: ContentManagementState
}): EnumTableData => {
  return state.contentManagement.providerEnumData
}

export const selectPracticeEnumTableData = (state: {
  contentManagement: ContentManagementState
}): EnumTableData => {
  return state.contentManagement.practiceEnumData
}

export const selectContentDataStatus = (state: {
  contentManagement: ContentManagementState
}): DataStatus => {
  return state.contentManagement.contentDataStatus
}

export const selectProviderTableDataStatus = (state: {
  contentManagement: ContentManagementState
}): DataStatus => {
  return state.contentManagement.providerTableDataStatus
}

export const selectPracticeTableDataStatus = (state: {
  contentManagement: ContentManagementState
}): DataStatus => {
  return state.contentManagement.practiceTableDataStatus
}

// New lazy loading selectors
export const selectIsCollectionLoaded =
  (collectionName: keyof Content) =>
  (state: { contentManagement: ContentManagementState }): boolean => {
    return state.contentManagement.loadedCollections[collectionName] ?? false
  }

export const selectCollectionStatus =
  (collectionName: keyof Content) =>
  (state: { contentManagement: ContentManagementState }): DataStatus => {
    return (
      state.contentManagement.collectionLoadingStatus[collectionName] ??
      DataStatus.PENDING
    )
  }

// GraphQL override selectors
export const selectIsContentOverridden = (state: {
  contentManagement: ContentManagementState
}): boolean => {
  return state.contentManagement.isContentOverridden
}

export const selectOverrideContent = (state: {
  contentManagement: ContentManagementState
}): Content | null => {
  return state.contentManagement.overrideContent
}

export default contentManagementSlice.reducer
